/**
 * The empty-state greeting — the first thing a user reads in a new chat.
 *
 * Picked at random per chat rather than fixed, so opening Brew doesn't feel
 * like replaying the same screen. The pools are split by time of day: a line
 * that matches the user's clock ("Still up?" at 1am) reads as attentive and
 * costs nothing but a Date lookup. Deliberately a curated list, not a model
 * call — the hero is the first thing painted, and a greeting isn't worth a
 * token spend plus a round-trip on every new chat.
 *
 * Tone rule: warm and plain. These are greetings, not poetry — a line the
 * reader has to decode buys less than it costs. `{name}` is substituted with
 * the user's first name.
 */

type Bucket = 'morning' | 'afternoon' | 'evening' | 'night';

/** Time-appropriate lines. Kept small per bucket — ANY widens the pool. */
const BY_TIME: Record<Bucket, string[]> = {
  morning: [
    'Morning, {name}. What are we starting with?',
    'Good morning, {name}.',
    'Early start, {name}?',
    'Morning, {name} — what’s first?',
  ],
  afternoon: [
    'Afternoon, {name}. What’s next?',
    'Good afternoon, {name}.',
    'Back at it, {name}?',
    'What’s the afternoon looking like, {name}?',
  ],
  evening: [
    'Evening, {name}. What are we finishing?',
    'Good evening, {name}.',
    'Wrapping up, {name}?',
    'Evening, {name} — what’s left?',
  ],
  night: [
    'Still up, {name}?',
    'Late one tonight, {name}?',
    'Working late, {name}?',
    'Up late, {name} — what’s on your mind?',
  ],
};

/** Lines that work at any hour. The original greeting stays in the mix. */
const ANY = [
  'Hi {name}, what’s on your mind?',
  'What are we working on, {name}?',
  'Where should we start, {name}?',
  'What can I help with, {name}?',
  'Ready when you are, {name}.',
  'Hey {name}, what do you need?',
  'What’s the task, {name}?',
];

/** Local hour → bucket. Night wraps past midnight, so it's the fallthrough. */
function bucketFor(hour: number): Bucket {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
}

/**
 * A greeting for the empty-state hero.
 *
 * Call once per chat and memoize the result — this is deliberately
 * non-deterministic, so calling it bare during render would re-roll the line
 * on every keystroke and stream chunk.
 *
 * @param firstName interpolated into `{name}`; falls back to "there".
 * @param now       injectable for tests; defaults to the user's local clock.
 */
export function pickGreeting(firstName: string, now: Date = new Date()): string {
  const pool = [...BY_TIME[bucketFor(now.getHours())], ...ANY];
  const line = pool[Math.floor(Math.random() * pool.length)]!;
  return line.replace('{name}', firstName || 'there');
}